import { useState, useEffect, useCallback, useRef } from "react"
import { useTranslation } from "react-i18next"
import { Icon } from "../icons/Icon"
import { processImageUrl, ImageFormat } from "@/utils/process-image-url"
import { ReactCompareSlider, ReactCompareSliderHandle } from "react-compare-slider"
import { Button } from "../ui/Button"
import { Pair, StylePreview } from "./StylePreviewPanel"
import { CompareElement } from "./CompareElement"
import { Spinner } from "../ui/Spinner"
import clsx from "clsx"

export interface CompareDialogProps {
	beforeAfterPhotos: Pair<StylePreview>[]
	initialIndex?: number
	onNavigate?: (index: number) => void
	onClose?: () => void
}

export const CompareDialog = ({
	beforeAfterPhotos,
	initialIndex = 0,
	onNavigate,
	onClose,
}: CompareDialogProps) => {
	const { t } = useTranslation("components")
	const [currentIndex, setCurrentIndex] = useState(initialIndex)
	const [loadedImages, setLoadedImages] = useState<Map<string, number>>(() => new Map()) // <url, ratio>
	const [failedLoadingImage, setFailedLoadingImage] = useState<Set<string>>(new Set())

	const loadingImagesRef = useRef<Set<string>>(new Set())

	const currentPair = beforeAfterPhotos[currentIndex]
	const hasMultiple = beforeAfterPhotos.length > 1

	const getProcessedUrl = useCallback((preview: StylePreview): string | null => {
		if (preview.url) {
			return processImageUrl({ url: preview.url, format: ImageFormat.AVIF })
		} else {
			return null
		}
	}, [])

	const preloadImage = useCallback(
		(preview: StylePreview) => {
			const processedUrl = getProcessedUrl(preview)

			if (!processedUrl || loadingImagesRef.current.has(processedUrl)) {
				return
			}

			setLoadedImages((prev) => {
				if (prev.has(processedUrl)) {
					return prev
				}
				loadingImagesRef.current.add(processedUrl)

				const img = new Image()
				img.crossOrigin = "anonymous"
				img.onload = () => {
					const ratio = img.naturalWidth / img.naturalHeight
					setLoadedImages((prev) => new Map(prev).set(processedUrl, ratio))
					loadingImagesRef.current.delete(processedUrl)
				}
				img.onerror = () => {
					setFailedLoadingImage((prev) => new Set(prev).add(processedUrl))
					loadingImagesRef.current.delete(processedUrl)
				}
				img.src = processedUrl

				return prev
			})
		},
		[getProcessedUrl],
	)

	const isCachedImage = (preview: StylePreview) => {
		const processedUrl = getProcessedUrl(preview)
		return !!processedUrl && loadedImages.has(processedUrl)
	}

	const isLoadingFailed = (previews: Pair<StylePreview>) => {
		const beforeUrl = getProcessedUrl(previews.before)
		if (!beforeUrl) {
			return true
		}
		if (failedLoadingImage.has(beforeUrl)) {
			return true
		}
		const afterUrl = getProcessedUrl(previews.after)
		if (!afterUrl) {
			return true
		}
		if (failedLoadingImage.has(afterUrl)) {
			return true
		}

		return false
	}

	const getRatio = (previews: Pair<StylePreview>): number => {
		const beforeUrl = getProcessedUrl(previews.before)
		if (beforeUrl) {
			const ratio = loadedImages.get(beforeUrl)
			if (ratio !== undefined) {
				return ratio
			}
		}

		const afterUrl = getProcessedUrl(previews.after)
		if (afterUrl) {
			const ratio = loadedImages.get(afterUrl)
			if (ratio !== undefined) {
				return ratio
			}
		}

		return 3 / 2
	}

	const isLoaded =
		!!currentPair && isCachedImage(currentPair.before) && isCachedImage(currentPair.after)
	const isFailed = !isLoaded && !!currentPair && isLoadingFailed(currentPair)

	useEffect(() => {
		if (!currentPair) return

		setFailedLoadingImage(new Set())
		preloadImage(currentPair.before)
		preloadImage(currentPair.after)

		if (hasMultiple) {
			const prevIndex = currentIndex > 0 ? currentIndex - 1 : beforeAfterPhotos.length - 1
			const nextIndex = currentIndex < beforeAfterPhotos.length - 1 ? currentIndex + 1 : 0

			const prevPair = beforeAfterPhotos[prevIndex]
			const nextPair = beforeAfterPhotos[nextIndex]

			if (prevPair) {
				preloadImage(prevPair.before)
				preloadImage(prevPair.after)
			}
			if (nextPair) {
				preloadImage(nextPair.before)
				preloadImage(nextPair.after)
			}
		}
	}, [currentIndex, currentPair, hasMultiple, preloadImage, beforeAfterPhotos])

	const navigate = useCallback(
		(direction: "prev" | "next") => {
			setCurrentIndex((prev) => {
				if (!isLoaded && !isFailed) {
					return prev
				}

				const newIndex =
					direction === "prev"
						? prev > 0
							? prev - 1
							: beforeAfterPhotos.length - 1
						: prev < beforeAfterPhotos.length - 1
							? prev + 1
							: 0

				onNavigate?.(newIndex)
				return newIndex
			})
		},
		[beforeAfterPhotos.length, onNavigate, isLoaded, isFailed],
	)

	const handleBackdropClick = (e: React.MouseEvent) => {
		if (e.target === e.currentTarget) {
			onClose?.()
		}
	}

	const handleContentClick = (e: React.MouseEvent) => {
		e.stopPropagation()
	}

	useEffect(() => {
		if (!hasMultiple) return

		const handleKeyDown = (e: KeyboardEvent) => {
			if (e.key === "ArrowLeft") {
				navigate("prev")
			}
			if (e.key === "ArrowRight") {
				navigate("next")
			}
		}

		document.addEventListener("keydown", handleKeyDown)
		return () => document.removeEventListener("keydown", handleKeyDown)
	}, [hasMultiple, navigate])

	useEffect(() => {
		setCurrentIndex(initialIndex)
	}, [initialIndex])

	if (!currentPair) return null

	const aspectRatio = getRatio(currentPair)
	const isPortrait = aspectRatio <= 1

	return (
		<div
			className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4"
			onClick={handleBackdropClick}
		>
			<Button
				variant="text"
				size="custom"
				onClick={() => onClose?.()}
				className="absolute right-4 top-4 z-30 flex size-8 items-center justify-center text-base-500 hover:text-base-700 dark:text-base-300 dark:hover:text-base-50"
				trackingLabel="compare_close"
				aria-label={t("compareDialog.ariaCloseCompareView")}
			>
				<Icon name="close" size={24} />
			</Button>

			{hasMultiple && (
				<Button
					variant="text"
					size="custom"
					onClick={() => navigate("prev")}
					className="mr-6 flex size-10 items-center justify-center text-base-500 hover:text-base-700 dark:text-base-300 dark:hover:text-base-50"
					disabled={!isLoaded && !isFailed}
					trackingLabel="compare_previous"
					aria-label={t("compareDialog.ariaPreviousComparePhoto")}
				>
					<Icon name="left" size={24} />
				</Button>
			)}

			<div
				className={clsx("relative", isPortrait ? "h-4/5" : "w-3/5")}
				onClick={handleContentClick}
			>
				{!isLoaded && (
					<div className="absolute inset-0 z-20 flex items-center justify-center rounded-xl bg-black/50">
						{!isFailed && <Spinner size={24} />}
						{isFailed && (
							<div className="p-2 text-sm text-base-400">
								{t("compareDialog.unableToLoad")}
							</div>
						)}
					</div>
				)}

				<div
					className={clsx("overflow-hidden rounded-xl", isPortrait ? "h-full" : "w-full")}
					style={{
						aspectRatio: aspectRatio,
					}}
				>
					<ReactCompareSlider
						changePositionOnHover
						handle={
							<ReactCompareSliderHandle
								buttonStyle={{
									border: "2px solid",
									boxShadow: "none",
								}}
								linesStyle={{
									opacity: 0.8,
									boxShadow: "none",
								}}
							/>
						}
						position={50}
						className={isLoaded ? "opacity-100" : "pointer-events-none opacity-0"}
						style={{
							width: "100%",
							height: "100%",
						}}
						itemOne={
							<CompareElement
								source={getProcessedUrl(currentPair.before)}
								altText={t("compareDialog.altBefore")}
							/>
						}
						itemTwo={
							<CompareElement
								source={getProcessedUrl(currentPair.after)}
								altText={t("compareDialog.altAfter")}
							/>
						}
					/>
				</div>
			</div>

			{hasMultiple && (
				<Button
					variant="custom"
					size="custom"
					onClick={() => navigate("next")}
					className="ml-6 flex size-10 items-center justify-center text-base-500 hover:text-base-700 dark:text-base-300 dark:hover:text-base-50"
					disabled={!isLoaded && !isFailed}
					trackingLabel="compare_next"
					aria-label={t("compareDialog.ariaNextComparePhoto")}
				>
					<Icon name="right" size={24} />
				</Button>
			)}
		</div>
	)
}
